import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { admin, products as productsApi } from '@/utils/api';
import { toast } from 'sonner';
import { Users, Package, TrendingUp, CheckCircle, XCircle, Trash2, ShoppingCart, Edit, X, Star, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  const [pendingArtists, setPendingArtists] = useState([]);
  const [allArtists, setAllArtists] = useState([]);
  const [allBuyers, setAllBuyers] = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingArtist, setEditingArtist] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    if (!user || user.userType !== 'admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      if (activeTab === 'analytics') {
        const response = await admin.getAnalytics();
        setAnalytics(response.data);
      } else if (activeTab === 'artists') {
        const response = await admin.getPendingArtists();
        setPendingArtists(response.data);
      } else if (activeTab === 'all-artists') {
        const response = await admin.getAllArtists();
        setAllArtists(response.data);
      } else if (activeTab === 'all-buyers') {
        const response = await admin.getAllBuyers();
        setAllBuyers(response.data);
      } else if (activeTab === 'products') {
        const response = await admin.getPendingProducts();
        setPendingProducts(response.data);
      } else if (activeTab === 'all-products') {
        const response = await admin.getAllProducts();
        setAllProducts(response.data);
      } else if (activeTab === 'orders') {
        const response = await admin.getAllOrders();
        setAllOrders(response.data);
      } else if (activeTab === 'commission') {
        const response = await admin.getAllOrders();
        setAllOrders(response.data);
      } else if (activeTab === 'featured') {
        const response = await admin.getFeaturedProducts();
        setFeaturedProducts(response.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveArtist = async (artistId, approved) => {
    try {
      await admin.approveArtist(artistId, { approved });
      toast.success(`Artist ${approved ? 'approved' : 'rejected'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update artist status');
    }
  };

  const loadAllOrders = async () => {
    try {
      const response = await admin.getAllOrders();
      setAllOrders(response.data);
    } catch (error) {
      console.error('Failed to reload orders');
    }
  };

  const handleApproveProduct = async (productId, status) => {
    try {
      await admin.approveProduct(productId, { status });
      toast.success(`Product ${status}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update product status');
    }
  };

  const handleDeleteArtist = async (artistId, artistName) => {
    if (!window.confirm(`Are you sure you want to delete artist "${artistName}"? This will also delete all their products.`)) {
      return;
    }
    try {
      await admin.deleteArtist(artistId);
      toast.success('Artist deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete artist');
    }
  };

  const handleDeleteProduct = async (productId, productTitle) => {
    if (!window.confirm(`Are you sure you want to delete product "${productTitle}"?`)) {
      return;
    }
    try {
      await admin.deleteProduct(productId);
      toast.success('Product deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleExportArtists = async () => {
    try {
      // Get all artists data
      const response = await admin.getAllArtists();
      const artists = response.data;
      
      // Get all products to calculate total inventory value per artist
      const productsResponse = await admin.getAllProducts();
      const allProducts = productsResponse.data;

      // Create CSV content
      const headers = ['Name', 'Email', 'Phone', 'Category', 'Total Products', 'Total Inventory Value (INR)', 'Status', 'Registration Date', 'GST Number', 'PAN Number'];
      const csvContent = [
        headers.join(','),
        ...artists.map(artist => {
          // Calculate artist's products and total value
          const artistProducts = allProducts.filter(p => p.artistId === artist.id);
          const totalProducts = artistProducts.length;
          const totalValue = artistProducts.reduce((sum, product) => {
            const basePrice = product.price || 0;
            const stock = product.stock || 0;
            return sum + (basePrice * stock);
          }, 0);
          
          return [
            `"${artist.name || ''}"`,
            `"${artist.email || ''}"`,
            `"${artist.phone || 'N/A'}"`,
            `"${artist.category || 'N/A'}"`,
            `"${totalProducts}"`,
            `"${totalValue.toFixed(2)}"`,
            `"${artist.isApproved ? 'Approved' : 'Pending'}"`,
            `"${artist.createdAt ? new Date(artist.createdAt).toLocaleDateString() : 'N/A'}"`,
            `"${artist.gstNumber || 'N/A'}"`,
            `"${artist.panNumber || 'N/A'}"`
          ].join(',');
        })
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `artcardo_artists_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Artists report exported successfully!');
    } catch (error) {
      toast.error('Failed to export artists report');
      console.error('Export error:', error);
    }
  };

  const handleExportBuyers = async () => {
    try {
      // Get all buyers data
      const response = await admin.getAllBuyers();
      const buyers = response.data;

      // Create CSV content
      const headers = ['Name', 'Email', 'Phone', 'Registration Date', 'Status'];
      const csvContent = [
        headers.join(','),
        ...buyers.map(buyer => [
          `"${buyer.name || ''}"`,
          `"${buyer.email || ''}"`,
          `"${buyer.phone || 'N/A'}"`,
          `"${buyer.createdAt ? new Date(buyer.createdAt).toLocaleDateString() : 'N/A'}"`,
          `"${buyer.isActive ? 'Active' : 'Inactive'}"`
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `artcardo_buyers_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Buyers report exported successfully!');
    } catch (error) {
      toast.error('Failed to export buyers report');
      console.error('Export error:', error);
    }
  };

  const handleExportProducts = async () => {
    try {
      // Get all products data
      const response = await admin.getAllProducts();
      const products = response.data;

      // Create CSV content
      const headers = ['Title', 'Artist Name', 'Category', 'Base Price (INR)', 'GST Rate (%)', 'GST Amount (INR)', 'Total Price (INR)', 'Stock', 'Status', 'Created Date'];
      const csvContent = [
        headers.join(','),
        ...products.map(product => {
          const basePrice = product.price || 0;
          const gstRate = product.gstRate || 18;
          const gstAmount = (basePrice * gstRate) / 100;
          const totalPrice = basePrice + gstAmount;
          
          return [
            `"${product.title || ''}"`,
            `"${product.artistName || 'N/A'}"`,
            `"${product.category || 'N/A'}"`,
            `"${basePrice.toFixed(2)}"`,
            `"${gstRate}"`,
            `"${gstAmount.toFixed(2)}"`,
            `"${totalPrice.toFixed(2)}"`,
            `"${product.stock || 0}"`,
            `"${product.isApproved ? 'Approved' : 'Pending'}"`,
            `"${product.createdAt ? new Date(product.createdAt).toLocaleDateString() : 'N/A'}"`
          ].join(',');
        })
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `artcardo_products_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Products report exported successfully!');
    } catch (error) {
      toast.error('Failed to export products report');
      console.error('Export error:', error);
    }
  };

  const handleDeleteBuyer = async (buyerId, buyerName) => {
    if (!window.confirm(`Are you sure you want to delete buyer "${buyerName}"?`)) {
      return;
    }
    try {
      await admin.deleteBuyer(buyerId);
      toast.success('Buyer deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete buyer');
    }
  };

  // Edit Artist Handler
  const handleSaveArtist = async () => {
    if (!editingArtist) return;
    setSaving(true);
    try {
      await admin.updateArtist(editingArtist.id, editingArtist);
      toast.success('Artist updated successfully');
      setEditingArtist(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update artist');
    } finally {
      setSaving(false);
    }
  };

  // Edit Product Handler
  const handleSaveProduct = async () => {
    if (!editingProduct) return;
    setSaving(true);
    try {
      await admin.updateProduct(editingProduct.id, editingProduct);
      toast.success('Product updated successfully');
      setEditingProduct(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-dashboard">
      <Navbar />
      
      <div style={{paddingTop: "180px"}}>
        <section className="section">
          <h1 className="section-title" data-testid="dashboard-title">Admin Dashboard</h1>
          
          <div className="max-w-6xl mx-auto">
            <div className="flex gap-4 mb-8 justify-center flex-wrap">
              <Button
                onClick={() => setActiveTab('analytics')}
                variant={activeTab === 'analytics' ? 'default' : 'outline'}
                data-testid="analytics-tab"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Analytics
              </Button>
              <Button
                onClick={() => setActiveTab('artists')}
                variant={activeTab === 'artists' ? 'default' : 'outline'}
                data-testid="artists-tab"
              >
                <Users className="w-4 h-4 mr-2" />
                Pending Artists
              </Button>
              <Button
                onClick={() => setActiveTab('all-artists')}
                variant={activeTab === 'all-artists' ? 'default' : 'outline'}
                data-testid="all-artists-tab"
              >
                <Users className="w-4 h-4 mr-2" />
                All Artists
              </Button>
              <Button
                onClick={() => setActiveTab('all-buyers')}
                variant={activeTab === 'all-buyers' ? 'default' : 'outline'}
                data-testid="all-buyers-tab"
              >
                <Users className="w-4 h-4 mr-2" />
                All Buyers
              </Button>
              <Button
                onClick={() => setActiveTab('products')}
                variant={activeTab === 'products' ? 'default' : 'outline'}
                data-testid="products-tab"
              >
                <Package className="w-4 h-4 mr-2" />
                Pending Products
              </Button>
              <Button
                onClick={() => setActiveTab('all-products')}
                variant={activeTab === 'all-products' ? 'default' : 'outline'}
                data-testid="all-products-tab"
              >
                <Package className="w-4 h-4 mr-2" />
                All Products
              </Button>
              <Button
                onClick={() => setActiveTab('featured')}
                variant={activeTab === 'featured' ? 'default' : 'outline'}
                data-testid="featured-tab"
                className={activeTab === 'featured' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
              >
                <Star className="w-4 h-4 mr-2" />
                Featured
              </Button>
              <Button
                onClick={() => setActiveTab('orders')}
                variant={activeTab === 'orders' ? 'default' : 'outline'}
                data-testid="orders-tab"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                All Orders
              </Button>
              <Button
                onClick={() => setActiveTab('commission')}
                variant={activeTab === 'commission' ? 'default' : 'outline'}
                data-testid="commission-tab"
                className={activeTab === 'commission' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Commission
              </Button>
            </div>

            {activeTab === 'analytics' && analytics && (
              <div data-testid="analytics-section">
                <h2 className="text-2xl font-semibold mb-6">Platform Analytics</h2>
                <p className="text-gray-500 mb-4 text-sm">Click on any card to view details</p>
                
                {/* Reactivate All Button */}
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-green-800">Reactivate All Artists & Products</h4>
                    <p className="text-sm text-green-600">Click to restore any deactivated artists and their products</p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reactivate-all`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`,
                            'Content-Type': 'application/json'
                          }
                        });
                        const data = await response.json();
                        if (response.ok) {
                          toast.success(`Reactivated ${data.artistsReactivated} artists and ${data.productsReactivated} products`);
                          loadData();
                        } else {
                          toast.error(data.detail || 'Failed to reactivate');
                        }
                      } catch (error) {
                        toast.error('Failed to reactivate');
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Reactivate All
                  </Button>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div 
                    className="card text-center cursor-pointer hover:shadow-lg hover:border-[#ef4444] transition-all duration-200"
                    onClick={() => setActiveTab('all-artists')}
                  >
                    <Users className="w-12 h-12 text-[#ef4444] mx-auto mb-3" />
                    <h3 className="text-3xl font-bold mb-2">{analytics.totalArtists}</h3>
                    <p className="text-gray-600">Total Artists</p>
                    <p className="text-xs text-[#ef4444] mt-2">Click to view</p>
                  </div>
                  <div 
                    className="card text-center cursor-pointer hover:shadow-lg hover:border-[#ef4444] transition-all duration-200"
                    onClick={() => setActiveTab('all-buyers')}
                  >
                    <Users className="w-12 h-12 text-[#ef4444] mx-auto mb-3" />
                    <h3 className="text-3xl font-bold mb-2">{analytics.totalBuyers}</h3>
                    <p className="text-gray-600">Total Buyers</p>
                    <p className="text-xs text-[#ef4444] mt-2">Click to view</p>
                  </div>
                  <div 
                    className="card text-center cursor-pointer hover:shadow-lg hover:border-[#ef4444] transition-all duration-200"
                    onClick={() => setActiveTab('all-products')}
                  >
                    <Package className="w-12 h-12 text-[#ef4444] mx-auto mb-3" />
                    <h3 className="text-3xl font-bold mb-2">{analytics.totalProducts}</h3>
                    <p className="text-gray-600">Total Products</p>
                    <p className="text-xs text-[#ef4444] mt-2">Click to view →</p>
                  </div>
                  <div 
                    className="card text-center cursor-pointer hover:shadow-lg hover:border-[#ef4444] transition-all duration-200"
                    onClick={() => setActiveTab('orders')}
                  >
                    <TrendingUp className="w-12 h-12 text-[#ef4444] mx-auto mb-3" />
                    <h3 className="text-3xl font-bold mb-2">{analytics.totalOrders}</h3>
                    <p className="text-gray-600">Total Orders</p>
                    <p className="text-xs text-[#ef4444] mt-2">Click to view →</p>
                  </div>
                  <div 
                    className="card text-center cursor-pointer hover:shadow-lg hover:border-[#ef4444] transition-all duration-200"
                    onClick={() => setActiveTab('orders')}
                  >
                    <TrendingUp className="w-12 h-12 text-[#ef4444] mx-auto mb-3" />
                    <h3 className="text-3xl font-bold mb-2">₹{analytics.totalSales.toLocaleString()}</h3>
                    <p className="text-gray-600">Total Sales</p>
                    <p className="text-xs text-[#ef4444] mt-2">Click to view →</p>
                  </div>
                  <div 
                    className="card text-center cursor-pointer hover:shadow-lg hover:border-green-600 transition-all duration-200"
                    onClick={() => setActiveTab('commission')}
                  >
                    <TrendingUp className="w-12 h-12 text-green-600 mx-auto mb-3" />
                    <h3 className="text-3xl font-bold mb-2">₹{analytics.totalCommission.toLocaleString()}</h3>
                    <p className="text-gray-600">Platform Commission</p>
                    <p className="text-xs text-green-600 mt-2">Click to view →</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'artists' && (
              <div data-testid="artists-section">
                <h2 className="text-2xl font-semibold mb-6">Pending Artist Approvals</h2>
                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : pendingArtists.length > 0 ? (
                  <div className="space-y-4" data-testid="artists-list">
                    {pendingArtists.map((artist) => (
                      <div key={artist.id} className="card" data-testid={`artist-${artist.id}`}>
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold mb-2">{artist.name}</h3>
                            <p className="text-sm text-gray-600">Email: {artist.email}</p>
                            <p className="text-sm text-gray-600">Phone: {artist.phone || 'N/A'}</p>
                            <p className="text-sm text-gray-600">GST: {artist.gstNumber || 'N/A'}</p>
                            <p className="text-sm text-gray-600">PAN: {artist.panNumber || 'N/A'}</p>
                            {artist.bio && (
                              <p className="text-sm text-gray-600 mt-2">
                                <strong>Bio:</strong> {artist.bio}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => handleApproveArtist(artist.id, true)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`approve-artist-${artist.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              onClick={() => handleApproveArtist(artist.id, false)}
                              variant="destructive"
                              data-testid={`reject-artist-${artist.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                            <Button
                              onClick={() => handleDeleteArtist(artist.id, artist.name)}
                              variant="outline"
                              className="border-red-600 text-red-600 hover:bg-red-50"
                              data-testid={`delete-artist-${artist.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12" data-testid="no-artists">
                    <p className="text-gray-500">No pending artist approvals</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'products' && (
              <div data-testid="products-section">
                <h2 className="text-2xl font-semibold mb-6">Pending Product Approvals</h2>
                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : pendingProducts.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-6" data-testid="products-list">
                    {pendingProducts.map((product) => (
                      <div key={product.id} className="card" data-testid={`product-${product.id}`}>
                        <div className="flex gap-4 mb-4">
                          <img
                            src={productsApi.getImage(product.images[0])}
                            alt={product.title}
                            className="w-32 h-32 object-cover rounded"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{product.title}</h3>
                            <p className="text-sm text-gray-600">by {product.artistName}</p>
                            <p className="text-sm text-gray-600">{product.category}</p>
                            <p className="text-lg font-bold text-[#ef4444] mt-1">
                              ₹{product.price.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">{product.description}</p>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveProduct(product.id, 'approved')}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            data-testid={`approve-product-${product.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleApproveProduct(product.id, 'rejected')}
                            variant="destructive"
                            className="flex-1"
                            data-testid={`reject-product-${product.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                          <Button
                            onClick={() => handleDeleteProduct(product.id, product.title)}
                            variant="outline"
                            className="border-red-600 text-red-600 hover:bg-red-50"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12" data-testid="no-products">
                    <p className="text-gray-500">No pending product approvals</p>
                  </div>
                )}
              </div>
            )}


            {activeTab === 'all-artists' && (
              <div data-testid="all-artists-section">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold">All Artists</h2>
                  <Button
                    onClick={handleExportArtists}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Export to Excel
                  </Button>
                </div>
                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : allArtists.length > 0 ? (
                  <div className="space-y-4" data-testid="all-artists-list">
                    {allArtists.filter(artist => artist.isApproved).map((artist) => (
                      <div key={artist.id} className="card" data-testid={`artist-${artist.id}`}>
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold mb-2">{artist.name}</h3>
                            <p className="text-sm text-gray-600">Email: {artist.email}</p>
                            <p className="text-sm text-gray-600">Phone: {artist.phone || 'N/A'}</p>
                            <p className="text-sm text-gray-600">Category: {artist.category || 'N/A'}</p>
                            <p className="text-sm text-gray-600">Bio: {artist.bio?.substring(0, 100) || 'N/A'}...</p>
                            <p className="text-sm text-gray-600">Status: 
                              <span className="text-green-600 font-semibold"> Approved</span>
                            </p>
                            <p className="text-sm text-gray-600">Registered: {new Date(artist.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => setEditingArtist({...artist})}
                              variant="outline"
                              className="border-blue-500 text-blue-500 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Artist
                            </Button>
                            <Button
                              onClick={() => handleDeleteArtist(artist.id, artist.name)}
                              variant="destructive"
                              data-testid={`delete-artist-${artist.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Artist
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12" data-testid="no-all-artists">
                    <p className="text-gray-500">No approved artists found</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'all-products' && (
              <div data-testid="all-products-section">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold">All Products</h2>
                  <Button
                    onClick={handleExportProducts}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Export to Excel
                  </Button>
                </div>
                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : allProducts.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-6" data-testid="all-products-list">
                    {allProducts.map((product) => (
                      <div key={product.id} className="card" data-testid={`product-${product.id}`}>
                        <div className="flex gap-4 mb-4">
                          <img
                            src={productsApi.getImage(product.images[0])}
                            alt={product.title}
                            className="w-32 h-32 object-cover rounded"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{product.title}</h3>
                            <p className="text-sm text-gray-600">by {product.artistName}</p>
                            <p className="text-sm text-gray-600">{product.category}</p>
                            <p className="text-lg font-bold text-[#ef4444] mt-1">
                              ₹{product.price.toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-600">Stock: {product.stock || 1}</p>
                            <p className="text-sm mt-1">Status: 
                              <span className={product.status === 'approved' ? 'text-green-600 font-semibold' : 'text-orange-600 font-semibold'}>
                                {product.status === 'approved' ? ' Approved' : product.status === 'rejected' ? ' Rejected' : ' Pending'}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setEditingProduct({...product})}
                            variant="outline"
                            className="flex-1 border-blue-500 text-blue-500 hover:bg-blue-50"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Product
                          </Button>
                          <Button
                            onClick={() => handleDeleteProduct(product.id, product.title)}
                            variant="destructive"
                            className="flex-1"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12" data-testid="no-all-products">
                    <p className="text-gray-500">No products found</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'all-buyers' && (
              <div data-testid="all-buyers-section">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold">All Buyers</h2>
                  <Button
                    onClick={handleExportBuyers}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Export to Excel
                  </Button>
                </div>
                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : allBuyers.length > 0 ? (
                  <div className="space-y-4" data-testid="all-buyers-list">
                    {allBuyers.map((buyer) => (
                      <div key={buyer.id} className="card" data-testid={`buyer-${buyer.id}`}>
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold mb-2">{buyer.name}</h3>
                            <p className="text-sm text-gray-600">Email: {buyer.email}</p>
                            <p className="text-sm text-gray-600">Phone: {buyer.phone || 'N/A'}</p>
                            <p className="text-sm text-gray-600">
                              Registered: {new Date(buyer.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => handleDeleteBuyer(buyer.id, buyer.name)}
                              variant="destructive"
                              data-testid={`delete-buyer-${buyer.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Buyer
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12" data-testid="no-buyers">
                    <p className="text-gray-500">No buyers found</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'orders' && (
              <div data-testid="orders-section">
                <h2 className="text-2xl font-semibold mb-6">All Orders</h2>
                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : allOrders.length > 0 ? (
                  <div className="space-y-4" data-testid="orders-list">
                    {allOrders.map((order) => {
                      const sellerEarnings = (order.basePrice || order.amount) - (order.commission || 0);
                      const sellerGst = sellerEarnings * 0.18;
                      const sellerTotal = sellerEarnings + sellerGst;
                      
                      return (
                        <div key={order.id} className="card border-l-4 border-l-[#ef4444]" data-testid={`order-${order.id}`}>
                          <div className="flex flex-col gap-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="text-lg font-semibold">Order #{order.orderId || order.id?.slice(0, 8)}</h3>
                                <p className="text-sm text-gray-500">
                                  {new Date(order.createdAt).toLocaleString()}
                                </p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                                order.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {order.status || 'Pending'}
                              </span>
                            </div>
                            
                            <div className="grid md:grid-cols-3 gap-4 text-sm">
                              <div>
                                <p><strong>Product:</strong> {order.productTitle || 'N/A'}</p>
                                <p><strong>Quantity:</strong> {order.quantity || 1}</p>
                                <p><strong>Total Amount:</strong> ₹{(order.totalAmount || order.amount || 0).toLocaleString()}</p>
                              </div>
                              <div>
                                <p><strong>Buyer:</strong> {order.buyerName || 'N/A'}</p>
                                <p><strong>Email:</strong> {order.buyerEmail || 'N/A'}</p>
                                <p><strong>Phone:</strong> {order.buyerPhone || 'N/A'}</p>
                              </div>
                              <div className="bg-green-50 p-2 rounded">
                                <p className="text-xs text-gray-600">Seller Payout:</p>
                                <p className="font-bold text-green-700">₹{sellerTotal.toFixed(2)}</p>
                                <p className="text-xs text-gray-500">(After 15% commission + GST)</p>
                              </div>
                            </div>
                            
                            {order.shippingAddress && (
                              <div className="text-sm bg-orange-50 p-3 rounded border border-orange-200">
                                <p><strong>Shipping Address:</strong></p>
                                <p className="text-gray-600">{order.shippingAddress}</p>
                              </div>
                            )}
                            
                            {/* Order Workflow Status */}
                            <div className="flex flex-wrap gap-2 items-center text-xs">
                              {order.sellerInvoiceUrl ? (
                                <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Seller Invoice Uploaded</span>
                              ) : (
                                <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Awaiting Seller Invoice</span>
                              )}
                              
                              {order.packingSlipUrl ? (
                                <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Packing Slip Sent</span>
                              ) : order.sellerInvoiceUrl ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs h-7"
                                  onClick={async () => {
                                    try {
                                      await admin.generatePackingSlip(order.id);
                                      toast.success('Packing slip generated and sent to seller!');
                                      loadAllOrders();
                                    } catch (error) {
                                      toast.error('Failed to generate packing slip');
                                    }
                                  }}
                                >
                                  Generate Packing Slip
                                </Button>
                              ) : null}
                              
                              {order.trackingNumber && (
                                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">Tracking: {order.trackingNumber}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12" data-testid="no-orders">
                    <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No orders found</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'featured' && (
              <div data-testid="featured-section">
                <h2 className="text-2xl font-semibold mb-6">Featured Products Management</h2>
                <p className="text-gray-600 mb-4">
                  Manage which products appear on the homepage and their display order. Featured products will be shown first.
                </p>
                
                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : (
                  <div className="space-y-4">
                    {/* Featured Products */}
                    <div className="mb-6">
                      <h3 className="font-semibold text-lg mb-3 flex items-center">
                        <Star className="w-5 h-5 text-yellow-500 mr-2 fill-yellow-500" />
                        Currently Featured ({featuredProducts.filter(p => p.isFeatured).length})
                      </h3>
                      <div className="space-y-2">
                        {featuredProducts
                          .filter(p => p.isFeatured)
                          .sort((a, b) => (a.featuredOrder || 999) - (b.featuredOrder || 999))
                          .map((product, index) => (
                            <div key={product.id} className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <span className="font-bold text-yellow-700 w-8">#{index + 1}</span>
                              <img 
                                src={productsApi.getImage(product.images?.[0])} 
                                alt={product.title}
                                className="w-16 h-16 object-cover rounded"
                              />
                              <div className="flex-1">
                                <p className="font-medium">{product.title}</p>
                                <p className="text-sm text-gray-500">by {product.artistName} • ₹{product.price?.toLocaleString()}</p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={index === 0}
                                  onClick={async () => {
                                    try {
                                      // Get all featured products sorted
                                      const featured = featuredProducts
                                        .filter(p => p.isFeatured)
                                        .sort((a, b) => (a.featuredOrder || 999) - (b.featuredOrder || 999));
                                      
                                      if (index > 0) {
                                        // Swap with previous product
                                        const updates = [
                                          { id: featured[index].id, order: index },
                                          { id: featured[index - 1].id, order: index + 1 }
                                        ];
                                        await admin.reorderFeatured(updates);
                                        toast.success('Product moved up');
                                        loadData();
                                      }
                                    } catch (error) {
                                      toast.error('Failed to reorder');
                                    }
                                  }}
                                >
                                  <ArrowUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={index === featuredProducts.filter(p => p.isFeatured).length - 1}
                                  onClick={async () => {
                                    try {
                                      // Get all featured products sorted
                                      const featured = featuredProducts
                                        .filter(p => p.isFeatured)
                                        .sort((a, b) => (a.featuredOrder || 999) - (b.featuredOrder || 999));
                                      
                                      if (index < featured.length - 1) {
                                        // Swap with next product
                                        const updates = [
                                          { id: featured[index].id, order: index + 2 },
                                          { id: featured[index + 1].id, order: index + 1 }
                                        ];
                                        await admin.reorderFeatured(updates);
                                        toast.success('Product moved down');
                                        loadData();
                                      }
                                    } catch (error) {
                                      toast.error('Failed to reorder');
                                    }
                                  }}
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={async () => {
                                    await admin.toggleFeatured(product.id);
                                    toast.success('Product removed from featured');
                                    loadData();
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        {featuredProducts.filter(p => p.isFeatured).length === 0 && (
                          <p className="text-gray-500 text-center py-4">No featured products yet. Add some below!</p>
                        )}
                      </div>
                    </div>
                    
                    {/* All Products to Feature */}
                    <div>
                      <h3 className="font-semibold text-lg mb-3">All Approved Products</h3>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {featuredProducts
                          .filter(p => !p.isFeatured)
                          .map(product => (
                            <div key={product.id} className="flex items-center gap-3 p-3 bg-gray-50 border rounded-lg">
                              <img 
                                src={productsApi.getImage(product.images?.[0])} 
                                alt={product.title}
                                className="w-12 h-12 object-cover rounded"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{product.title}</p>
                                <p className="text-xs text-gray-500">₹{product.price?.toLocaleString()}</p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-yellow-600 hover:bg-yellow-50"
                                onClick={async () => {
                                  await admin.toggleFeatured(product.id);
                                  toast.success('Product added to featured!');
                                  loadData();
                                }}
                              >
                                <Star className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'commission' && (
              <div data-testid="commission-section">
                <h2 className="text-2xl font-semibold mb-6">Platform Commission Details</h2>
                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : allOrders.length > 0 ? (
                  <>
                    {/* Commission Summary */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                      <div className="grid md:grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-gray-600">Total Orders</p>
                          <p className="text-2xl font-bold text-green-700">{allOrders.length}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Sales</p>
                          <p className="text-2xl font-bold text-green-700">
                            ₹{allOrders.reduce((sum, order) => sum + (order.amount || 0), 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Commission (15%)</p>
                          <p className="text-2xl font-bold text-green-700">
                            ₹{allOrders.reduce((sum, order) => sum + ((order.commission) || (order.amount || 0) * 0.15), 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Commission Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border p-3 text-left">Date</th>
                            <th className="border p-3 text-left">Product</th>
                            <th className="border p-3 text-left">Customer</th>
                            <th className="border p-3 text-right">Sale Value</th>
                            <th className="border p-3 text-right">Commission (10%)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allOrders.map((order) => (
                            <tr key={order.id} className="hover:bg-gray-50">
                              <td className="border p-3">
                                {new Date(order.createdAt).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </td>
                              <td className="border p-3">
                                <p className="font-medium">{order.productTitle || 'N/A'}</p>
                                <p className="text-xs text-gray-500">Qty: {order.quantity || 1}</p>
                              </td>
                              <td className="border p-3">
                                <p className="font-medium">{order.buyerName || 'N/A'}</p>
                                <p className="text-xs text-gray-500">{order.buyerEmail || ''}</p>
                              </td>
                              <td className="border p-3 text-right font-medium">
                                ₹{(order.amount || 0).toLocaleString()}
                              </td>
                              <td className="border p-3 text-right font-medium text-green-600">
                                ₹{((order.amount || 0) * 0.1).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-green-100 font-bold">
                            <td className="border p-3" colSpan="3">Total</td>
                            <td className="border p-3 text-right">
                              ₹{allOrders.reduce((sum, order) => sum + (order.amount || 0), 0).toLocaleString()}
                            </td>
                            <td className="border p-3 text-right text-green-700">
                              ₹{allOrders.reduce((sum, order) => sum + ((order.amount || 0) * 0.1), 0).toLocaleString()}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12" data-testid="no-commission">
                    <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No commission data available</p>
                    <p className="text-sm text-gray-400 mt-2">Commission will appear once orders are completed</p>
                  </div>
                )}
              </div>
            )}

          </div>
        </section>
      </div>

      {/* Edit Artist Modal */}
      {editingArtist && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-semibold">Edit Artist Profile</h2>
              <button onClick={() => setEditingArtist(null)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingArtist.name || ''}
                  onChange={(e) => setEditingArtist({...editingArtist, name: e.target.value})}
                  className="w-full border rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editingArtist.email || ''}
                  onChange={(e) => setEditingArtist({...editingArtist, email: e.target.value})}
                  className="w-full border rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  value={editingArtist.phone || ''}
                  onChange={(e) => setEditingArtist({...editingArtist, phone: e.target.value})}
                  className="w-full border rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={editingArtist.category || ''}
                  onChange={(e) => setEditingArtist({...editingArtist, category: e.target.value})}
                  className="w-full border rounded-md p-2"
                >
                  <option value="">Select Category</option>
                  <option value="Painting">Painting</option>
                  <option value="Sculpture">Sculpture</option>
                  <option value="Photography">Photography</option>
                  <option value="Digital Art">Digital Art</option>
                  <option value="Mixed Media">Mixed Media</option>
                  <option value="Crafts">Crafts</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  value={editingArtist.bio || ''}
                  onChange={(e) => setEditingArtist({...editingArtist, bio: e.target.value})}
                  className="w-full border rounded-md p-2 h-32"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                  <input
                    type="text"
                    value={editingArtist.gstNumber || ''}
                    onChange={(e) => setEditingArtist({...editingArtist, gstNumber: e.target.value})}
                    className="w-full border rounded-md p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                  <input
                    type="text"
                    value={editingArtist.panNumber || ''}
                    onChange={(e) => setEditingArtist({...editingArtist, panNumber: e.target.value})}
                    className="w-full border rounded-md p-2"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <Button variant="outline" onClick={() => setEditingArtist(null)}>
                Cancel
              </Button>
              <Button onClick={handleSaveArtist} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-semibold">Edit Product</h2>
              <button onClick={() => setEditingProduct(null)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editingProduct.title || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, title: e.target.value})}
                  className="w-full border rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Short Description (for homepage)</label>
                <input
                  type="text"
                  value={editingProduct.artDescription || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, artDescription: e.target.value})}
                  className="w-full border rounded-md p-2"
                  maxLength={200}
                />
                <p className="text-xs text-gray-500 mt-1">{(editingProduct.artDescription || '').length}/200 characters</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Description</label>
                <textarea
                  value={editingProduct.description || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                  className="w-full border rounded-md p-2 h-32"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                  <input
                    type="number"
                    value={editingProduct.price || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, price: parseFloat(e.target.value) || 0})}
                    className="w-full border rounded-md p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                  <input
                    type="number"
                    value={editingProduct.stock || 1}
                    onChange={(e) => setEditingProduct({...editingProduct, stock: parseInt(e.target.value) || 1})}
                    className="w-full border rounded-md p-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={editingProduct.category || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
                  className="w-full border rounded-md p-2"
                >
                  <option value="">Select Category</option>
                  <option value="Painting">Painting</option>
                  <option value="Sculpture">Sculpture</option>
                  <option value="Photography">Photography</option>
                  <option value="Digital Art">Digital Art</option>
                  <option value="Mixed Media">Mixed Media</option>
                  <option value="Crafts">Crafts</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medium</label>
                  <input
                    type="text"
                    value={editingProduct.medium || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, medium: e.target.value})}
                    className="w-full border rounded-md p-2"
                    placeholder="e.g., Oil on Canvas"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dimensions</label>
                  <input
                    type="text"
                    value={editingProduct.dimensions || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, dimensions: e.target.value})}
                    className="w-full border rounded-md p-2"
                    placeholder="e.g., 24 x 36 inches"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <Button variant="outline" onClick={() => setEditingProduct(null)}>
                Cancel
              </Button>
              <Button onClick={handleSaveProduct} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminDashboard;
